import React from "react";
import PropTypes from "prop-types";
import PatientsTable from "./patients-table";
import CoachStats from "./coach-stats";
import CoachOrdersTable from "./coach-dashboard-orders/table";
import PortalOrderFollowUpTable from "./portal-order-follow-up-table/table";
import ScDashboardTableSelect from "./table-select";
import { connect } from "react-redux";
import { selectors } from "reducers";
import AutodialerFilter from "./autodialer-filter";
import ActivityCenter from "./activity-center";
import PhoneNumberPrompt from "components/two-factor-auth/phone-number-prompt";
import VoicemailsTable from "components/voicemails-table";

const SleepCoachDashboard = ({ userId, userRole, userTeam, user }) => {
  const [patientsToView, setPatientsToView] =
    React.useState("eligible_patients");
  // The Legacy Autodialer table fills remaining space via a flex chain
  // (.s3-ui-header-footer / .table-container.scrollable use flex:1; min-height:0).
  // That chain only resolves if this wrapper has a definite height; the shared
  // content pane (section[role="main"] > div) is display:block, so flex:1 alone
  // leaves this wrapper content-sized and the table collapses to ~the header row.
  // Anchor a definite height for that view only so it scrolls inside the table
  // (single scrollbar) instead of collapsing. (S3DEV2-7376 follow-up)
  const isLegacyAutodialer = patientsToView === "legacy_autodialer";
  return (
    userRole != "ContractorAdministrator" && (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          ...(isLegacyAutodialer ? { height: "100%", minHeight: 0 } : {})
        }}
      >
        <PhoneNumberPrompt user={user} />
        <CoachStats />
        <ScDashboardTableSelect
          patientsToView={patientsToView}
          setPatientsToView={setPatientsToView}
        />
        {patientsToView === "activity_center" && <ActivityCenter />}
        {patientsToView === "eligible_patients" && <AutodialerFilter />}
        {patientsToView === "legacy_autodialer" && (
          <PatientsTable
            initialQuery={{
              sort: {
                by: "last_name",
                direction: "asc"
              },
              pagination: {
                page: 1,
                per: 500
              },
              filters: {
                eligible_product_lines: ["CPAP"],
                is_within_calling_hours: [true],                   
              }
            }}
          />
        )} 
        {patientsToView === "patients_with_backordered_orders" && (
          <CoachOrdersTable
            orderStatus="backordered"
            queryId="follow-up-orders-table"
            rowKey="id"
            initialQuery={{
              sort: {
                by: "submitted",
                direction: "desc"
              },
              pagination: {
                page: 1,
                per: 25
              },
              filters: {
                status: ["backordered"],
                order: ["all"],
                sleep_coach_assignment:
                  userRole == "SleepCoach" ? [userId] : [],
                team_name:
                  userRole == "SleepCoach"
                    ? [`${userTeam}^${user.team_id}`]
                    : []
              }
            }}
            apiId="follow-up-orders-table"
          />
        )}
        {patientsToView === "patients_with_followup_orders" && (
          <CoachOrdersTable
            orderStatus="follow_up"
            queryId="follow-up-orders-table"
            rowKey="id"
            initialQuery={{
              sort: {
                by: "submitted",
                direction: "desc"
              },
              pagination: {
                page: 1,
                per: 25
              },
              filters: {
                status:
                  userRole === "CompanyAdministrator" ||
                  userRole === "OfficeAdministrator"
                    ? ["follow_up"]
                    : ["follow_up", "internal_coach_follow_up"],
                order: ["all"],
                sleep_coach_assignment:
                  userRole == "SleepCoach" ? [userId] : [],
                team_name:
                  userRole == "SleepCoach"
                    ? [`${userTeam}^${user.team_id}`]
                    : []
              }
            }}
            apiId="follow-up-orders-table"
          />
        )}
        {patientsToView === "patients_with_new_orders" && (
          <CoachOrdersTable
            orderStatus="new"
            queryId="follow-up-orders-table"
            rowKey="id"
            initialQuery={{
              sort: {
                by: "submitted",
                direction: "desc"
              },
              pagination: {
                page: 1,
                per: 25
              },
              filters: {
                // TODO  double check status
                status: ["new"],
                sleep_coach_assignment:
                  userRole == "SleepCoach" ? [userId] : [],
                team_name: userRole == "SleepCoach" ? [userTeam] : []
              }
            }}
            apiId="follow-up-orders-table"
          />
        )}
        {patientsToView === "patients_with_incomplete_orders" && (
          <PortalOrderFollowUpTable
            queryId="portal-order-follow-up-table"
            rowKey="id"
            initialQuery={{
              sort: {
                by: "last_updated",
                direction: "desc"
              },
              pagination: {
                page: 1,
                per: 500
              },
              filters: {
                team_name: userRole == "SleepCoach" ? [userTeam] : [],
                worked: ["unworked"]
              }
            }}
            apiId="follow-up-orders-table"
          />
        )}
        {patientsToView === "voicemails" && (
          <VoicemailsTable/>
        )}
      </div>
    )
  );
};

export default connect(state => ({
  userId: selectors.getUserId(state),
  userTeam: selectors.getUserTeam(state),
  userRole: selectors.getUserRole(state),
  user: selectors.getUser(state)
}))(SleepCoachDashboard);

SleepCoachDashboard.propTypes = {
  userId: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired,
  userTeam: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired
};
